import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Snackbar } from "@material-ui/core";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";
import { useSubheader } from "../../../_metronic/layout";
import { SnackbarWrapper } from "../Snackbar/SnackbarWrapper";
import { BranchCourseCustomizationFilter } from "./Components/BranchCourseCustomizationFilter";

const headRows = [
  { id: "customization_id", label: "ID" },
  { id: "name", label: "Name" },
  { id: "courses", label: "Courses" },
  { id: "message_type", label: "Message Type" },
  { id: "message_details", label: "Message Details" },
  { id: "actions", label: "Actions" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

const API_URL = "api/branch-course-customization/list/";
const BRANCH_COURSES_URL = "/api/branch-course/";

export function BranchCourseCustomizationList({ history }) {
  const subheader = useSubheader();
  subheader.setTitle("Customize Stream Message");

  const classes = useStyles();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchCourses, setBranchCourses] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastVariant, setToastVariant] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const [filterValues, setFilterValues] = useState({ courseCode: "", active: "1" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const groupedData = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      const id = row.customization_id;
      if (!map.has(id)) {
        map.set(id, { ...row, courses: [] });
      }
      if (row.course_code) {
        map.get(id).courses.push(row.course_code);
      }
    });
    return Array.from(map.values());
  }, [data]);

  const courseCodes = useMemo(() => {
    const seen = new Set();
    return branchCourses
      .filter((c) => c.active_id == 1 && c.course_code)
      .filter((c) => {
        if (seen.has(c.course_code)) return false;
        seen.add(c.course_code);
        return true;
      })
      .map((c) => ({ code: c.course_code, name: c.course_name }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [branchCourses]);

  const filteredData = useMemo(() => {
    return groupedData.filter((row) => {
      const matchesCourse =
        !filterValues.courseCode || row.courses.includes(filterValues.courseCode);
      const matchesActive =
        filterValues.active === "" || row.active_id == filterValues.active;
      return matchesCourse && matchesActive;
    });
  }, [groupedData, filterValues]);

  const handleFilterData = (values) => {
    setFilterValues(values);
    setPage(0);
  };

  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );

  const authToken = useSelector(
    (state) => state.auth.authToken,
    shallowEqual
  );

  async function fetchCustomizations() {
    setLoading(true);
    try {
      const result = await axios(API_URL + branch.branch_id, {
        headers: { "x-auth-token": authToken },
      });
      setData(Array.isArray(result.data.data) ? result.data.data : []);
    } catch (error) {
      console.log(error);
      setData([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (branch?.branch_id) {
      fetchCustomizations();
      axios
        .get(BRANCH_COURSES_URL + branch.branch_id, {
          headers: { "x-auth-token": authToken },
        })
        .then((res) => setBranchCourses(res.data))
        .catch((err) => console.log(err));
    }
  }, [branch?.branch_id]);

  const handleActiveClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleModalConfirm = async () => {
    const newActiveId = selectedRow.active_id == 1 ? 0 : 1;
    setModalOpen(false);
    try {
      await axios.post(
        `api/branch-course-customization/set-active/${selectedRow.customization_id}`,
        { active_id: newActiveId },
        { headers: { "x-auth-token": authToken } }
      );
      setToastVariant("success");
      setToastMessage(
        `Customization set to ${newActiveId === 1 ? "Active" : "Inactive"}`
      );
      fetchCustomizations();
    } catch (error) {
      console.log(error);
      setToastVariant("error");
      setToastMessage("Something went wrong");
    }
    setToastOpen(true);
    setSelectedRow(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  return (
    <>
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={toastOpen}
      autoHideDuration={3000}
      onClose={() => setToastOpen(false)}
    >
      <SnackbarWrapper
        onClose={() => setToastOpen(false)}
        variant={toastVariant}
        message={toastMessage}
      />
    </Snackbar>

    <Dialog open={modalOpen} onClose={handleModalClose}>
      <DialogTitle>
        {selectedRow?.active_id == 1
          ? "Deactivate Customization"
          : "Activate Customization"}
      </DialogTitle>
      <DialogContent>
        {selectedRow?.active_id == 1
          ? `Set "${selectedRow?.name}" to Inactive?`
          : `Set "${selectedRow?.name}" to Active?`}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleModalClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleModalConfirm}
          color="primary"
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>

    <div className="card card-custom mb-5">
      <div className="card-header border-0 py-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Customize Stream Message Filters
          </span>
        </h3>
        <div className="card-toolbar">
          <button
            type="button"
            className="btn btn-primary font-weight-bolder font-size-sm"
            onClick={() => history.push("/branch-course-customization/create")}
          >
            New
          </button>
        </div>
      </div>
      <div className="card-body py-0">
        <BranchCourseCustomizationFilter
          filterData={handleFilterData}
          courseCodes={courseCodes}
        />
      </div>
    </div>

    <Card>
      <CardBody>
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.tableWrapper}>
              <Table className={classes.table} aria-labelledby="tableTitle">
                <TableHead>
                  <TableRow>
                    {headRows.map((row) => (
                      <TableCell key={row.id} align="center">
                        {row.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {loading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={headRows.length}>
                        <LinearProgress color="secondary" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={headRows.length} align="center">
                          <p className="text-muted my-3">
                            No customizations yet.
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredData
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => (
                          <TableRow key={index}>
                            <TableCell align="center">{row.customization_id}</TableCell>
                            <TableCell align="center">{row.name}</TableCell>
                            <TableCell>{row.courses.join(", ")}</TableCell>
                            <TableCell align="center">
                              {row.message_type}
                            </TableCell>
                            <TableCell align="center" style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.message_details?.replace(/<[^>]*>/g, "").trim()}
                            </TableCell>
                            <TableCell align="center">
                              <OverlayTrigger
                                overlay={
                                  <Tooltip>
                                    {row.active_id == 1
                                      ? "Deactivate"
                                      : "Activate"}
                                  </Tooltip>
                                }
                              >
                                <a
                                  className={`btn btn-icon btn-light btn-sm mr-1 ${
                                    row.active_id == 1
                                      ? "btn-hover-danger"
                                      : "btn-hover-success"
                                  }`}
                                  onClick={() => handleActiveClick(row)}
                                >
                                  <i
                                    className={`${
                                      row.active_id == 1
                                        ? "fa fa-eye text-success"
                                        : "fa fa-eye-slash text-muted"
                                    }`}
                                  />
                                </a>
                              </OverlayTrigger>
                              <OverlayTrigger
                                overlay={<Tooltip>Edit</Tooltip>}
                              >
                                <a
                                  className="btn btn-icon btn-light btn-hover-primary btn-sm mr-1"
                                  onClick={() =>
                                    history.push(
                                      `/branch-course-customization/edit/${row.customization_id}`
                                    )
                                  }
                                >
                                  <span className="svg-icon svg-icon-md svg-icon-primary">
                                    <i className="fa fa-pen text-primary" />
                                  </span>
                                </a>
                              </OverlayTrigger>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                )}
              </Table>
            </div>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{ "aria-label": "Previous Page" }}
              nextIconButtonProps={{ "aria-label": "Next Page" }}
              onChangePage={(e, newPage) => setPage(newPage)}
              onChangeRowsPerPage={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(0);
              }}
            />
          </Paper>
        </div>
      </CardBody>
    </Card>
    </>
  );
}
